import { useEffect, useState } from 'react';
import './app-header.css';
import { observer } from 'mobx-react-lite';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Select, Avatar, Dropdown, Badge, Button, Popover, List, Typography, Empty, Spin, Tooltip } from 'antd';
import { SwapOutlined, FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  BulbOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  CheckOutlined,
  DeleteOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { LocalizationModal } from '@/features/platform-localization/LocalizationModal';
import type { MenuProps } from 'antd';
import { useRootStore } from '../../app/providers/StoreProvider';
import { isStaffAccount, setStaffPrefersLegacy, staffPrefersLegacy } from '../../shared/stores/new-cabinet';
import { shellChrome } from '../../shared/stores/shell-chrome';
import { changelogStore } from '@/entities/changelog/changelog-store';
import type { MailNotification } from '@/entities/notifications/notifications-store';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ru';

dayjs.extend(relativeTime);
dayjs.locale('ru');

const { Text } = Typography;
const { Header } = Layout;

interface AppHeaderProps {
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * Переключатель объекта. Вынесен из шапки, чтобы экран, который
 * прячет шапку кабинета (Cockpit R3), ставил тот же контрол к себе.
 */
export const PlatformSwitcher = observer(function PlatformSwitcher({
  className = 'app-header-platform',
  variant,
  nameOnly = false,
}: {
  className?: string;
  variant?: 'outlined' | 'borderless' | 'filled';
  /** В поле — только название, без «· ID»; в списке ID остаётся. */
  nameOnly?: boolean;
}) {
  const store = useRootStore();
  const navigate = useNavigate();
  const { platforms, auth } = store;
  if (platforms.platformList.length === 0) return null;
  return (
          <Select
            value={platforms.activePlatform?.id}
            onChange={async (id) => {
              const oldId = platforms.activePlatform?.id;
              await auth.changePlatform(id);
              // Обновляем URL если он содержит /platforms/:oldId/
              const path = window.location.pathname;
              if (oldId && path.includes(`/platforms/${oldId}`)) {
                navigate(path.replace(`/platforms/${oldId}`, `/platforms/${id}`));
              }
            }}
            className={className}
            variant={variant}
            labelRender={
              nameOnly
                ? ({ value }) => {
                    const p = platforms.platformList.find((x) => x.id === value);
                    // ID рядом с названием и в шапке: поддержке нужен id
                    // объекта, по названию отель ищется долго.
                    return (
                      <>
                        {(p as any)?.short_name || p?.name || `#${value}`}
                        <span style={{ marginLeft: 8, fontWeight: 400, opacity: 0.55 }}>ID {value}</span>
                      </>
                    );
                  }
                : undefined
            }
            aria-label="Текущий объект"
            popupMatchSelectWidth={false}
            options={platforms.platformList.map((p) => ({
              // Показываем ID площадки рядом с названием — сотрудникам поддержки
              // нужен id объекта (напр. «Дом отдыха Дружба · ID 119»), т.к.
              // поддомен приложения (app-540443) не совпадает с id платформы.
              label: `${(p as any).short_name || p.name || `#${p.id}`} · ID ${p.id}`,
              value: p.id,
            }))}
          />
  );
});

/**
 * «Что нового», уведомления и профиль. Здесь же опрос уведомлений —
 * он идёт там, где показан колокольчик, в шапке кабинета или в шапке
 * Cockpit R3.
 */
export const HeaderActions = observer(function HeaderActions() {
  const store = useRootStore();
  const navigate = useNavigate();
  const { profile, auth, mailbox } = store;

  // Fetch notifications on mount and every 60 seconds
  useEffect(() => {
    mailbox.fetch();
    const interval = setInterval(() => mailbox.fetch(), 60_000);
    return () => clearInterval(interval);
  }, [mailbox]);

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Профиль',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Настройки',
      // В оболочке R3 — хаб «Настройки», в прежнем кабинете — «Организация».
      onClick: () => navigate(shellChrome.r3 ? '/settings' : '/settings/organization'),
    },
    // Новый кабинет: договоры, счета и акты HKeeper — «Подписка» (решение 03.11.2026).
    ...(shellChrome.r3
      ? [{
          key: 'subscription',
          icon: <FileTextOutlined />,
          label: 'Подписка',
          onClick: () => navigate('/settings/reports'),
        }]
      : []),
    // Суперадмин и поддержка: переключение «новый / старый интерфейс» для себя.
    ...(isStaffAccount()
      ? [{
          key: 'shell',
          icon: <SwapOutlined />,
          label: staffPrefersLegacy() ? 'Новый интерфейс' : 'Старый интерфейс',
          onClick: () => { setStaffPrefersLegacy(!staffPrefersLegacy()); window.location.reload(); },
        }]
      : []),
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Выйти',
      danger: true,
      onClick: () => auth.logout(),
    },
  ];

  const notificationsContent = (
    <div style={{ width: 'min(380px, calc(100vw - 24px))', maxHeight: 480, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #f0f0f0' }}>
        <Text strong>Сообщения</Text>
        {mailbox.unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => mailbox.markAllAsRead()}
          >
            Прочитать все
          </Button>
        )}
      </div>

      {/* List */}
      <div style={{ overflow: 'auto', flex: 1 }}>
        {mailbox.isLoading && mailbox.notifications.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : mailbox.notifications.length === 0 ? (
          <Empty description="Нет сообщений" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '40px 0' }} />
        ) : (
          <List
            dataSource={mailbox.notifications}
            renderItem={(item: MailNotification) => (
              <List.Item
                key={item.id}
                style={{
                  padding: '10px 12px',
                  cursor: 'pointer',
                  background: item.is_read ? 'transparent' : '#f0f7ff',
                  borderLeft: item.is_read ? '3px solid transparent' : '3px solid #1890ff',
                }}
                onClick={() => {
                  if (!item.is_read) {
                    mailbox.markAsRead([item.id]);
                  }
                }}
                actions={[
                  <Tooltip title="Удалить" key="del">
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        mailbox.deleteNotification(item.id);
                      }}
                    />
                  </Tooltip>,
                ]}
              >
                <List.Item.Meta
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong={!item.is_read} style={{ fontSize: 13 }} ellipsis>
                        {item.subject || 'Без темы'}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap', marginLeft: 8 }}>
                        {dayjs(item.created_at).fromNow()}
                      </Text>
                    </div>
                  }
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                      {item.text ? item.text.replace(/<[^>]*>/g, '').slice(0, 120) : ''}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="app-header-actions">
        {/* «Что нового» — в шапке, а не в сайдбаре: меню и так длинное,
            а лента нужна редко. Бейдж — записи новее последнего просмотра,
            гаснет при открытии страницы. */}
        <Tooltip title="Что нового">
          <Badge count={changelogStore.unreadCount} size="small" offset={[-2, 2]}>
            <Button
              type="text"
              icon={<BulbOutlined style={{ fontSize: 18 }} />}
              onClick={() => navigate('/changelog')}
              aria-label="Что нового"
            />
          </Badge>
        </Tooltip>

        <Popover
          content={notificationsContent}
          trigger="click"
          placement="bottomRight"
          open={mailbox.isOpen}
          onOpenChange={(v) => {
            mailbox.setOpen(v);
            if (v) mailbox.fetch();
          }}
          arrow={false}
          overlayInnerStyle={{ padding: 0 }}
        >
          <Badge count={mailbox.unreadCount} size="small" offset={[-2, 2]}>
            <Button aria-label="Уведомления" type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
          </Badge>
        </Popover>

        <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
          <div className="flex cursor-pointer items-center gap-2" style={{ minWidth: 0 }}>
            <Avatar size={32} icon={<UserOutlined />} style={{ flexShrink: 0 }} />
            {profile && (
              <span
                className="hidden text-sm sm:inline"
                style={{
                  color: 'var(--color-text-secondary)',
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {store.getUserDisplayName(profile)}
              </span>
            )}
          </div>
        </Dropdown>
    </div>
  );
});

/**
 * Кнопка-глобус «Языки объекта» с её модалкой — только в разделе
 * Консьержа. Общая для шапки кабинета и шапки R3.
 */
export const LocalizationAction = observer(function LocalizationAction() {
  const store = useRootStore();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  if (!location.pathname.startsWith('/concierge') || !store.platforms.activePlatformId) return null;
  return (
    <>
      <Tooltip title="Языки объекта">
        <Button
          type="text"
          aria-label="Языки объекта"
          icon={<GlobalOutlined style={{ fontSize: 18 }} />}
          onClick={() => setOpen(true)}
        />
      </Tooltip>
      <LocalizationModal open={open} onClose={() => setOpen(false)} />
    </>
  );
});

export const AppHeader = observer(function AppHeader({
  collapsed,
  onToggle,
}: AppHeaderProps) {
  return (
    <>
    <Header
      className="app-header"
      style={{
        background: 'var(--color-bg-primary)',
        minHeight: 'var(--header-height)',
        height: 'auto',
        borderBottom: '1px solid var(--color-border)',
        padding: '0 16px',
        position: 'sticky',
        top: 0,
        zIndex: 40,
      }}
    >
      {/* Левая часть: toggle + селектор платформы */}
      <div className="app-header-context">
        <Button
          type="text"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggle}
        />
        <PlatformSwitcher />
      </div>

      {/* Правая часть: локализация (только в Консьерже) + уведомления + профиль */}
      <div className="app-header-actions">
        <LocalizationAction />
        <HeaderActions />
      </div>
    </Header>

    </>
  );
});
